import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, RefreshCw, Handshake, Check, X, ExternalLink, DollarSign, Send, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { errMsg } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import ImageLightbox from '../components/ImageLightbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  submitted:         { bg: 'bg-yellow-500/15',  text: 'text-yellow-400',  border: 'border-yellow-500/40' },
  needs_review:      { bg: 'bg-orange-500/15',  text: 'text-orange-400',  border: 'border-orange-500/40' },
  accepted:          { bg: 'bg-blue-500/15',    text: 'text-blue-400',    border: 'border-blue-500/40' },
  awaiting_shipment: { bg: 'bg-blue-500/15',    text: 'text-blue-400',    border: 'border-blue-500/40' },
  in_transit:        { bg: 'bg-blue-500/10',    text: 'text-blue-300',    border: 'border-blue-500/30' },
  received:          { bg: 'bg-purple-500/15',  text: 'text-purple-300',  border: 'border-purple-500/40' },
  listed:            { bg: 'bg-tiffany-500/15', text: 'text-tiffany-500', border: 'border-tiffany-500/40' },
  sold:              { bg: 'bg-purple-500/15',  text: 'text-purple-400',  border: 'border-purple-500/40' },
  returned:          { bg: 'bg-zinc-500/15',    text: 'text-zinc-400',    border: 'border-zinc-500/40' },
  rejected:          { bg: 'bg-red-500/15',     text: 'text-red-400',     border: 'border-red-500/40' },
  expired:           { bg: 'bg-zinc-500/15',    text: 'text-zinc-400',    border: 'border-zinc-500/40' },
};

const STATUS_LABEL = {
  submitted: 'SUBMITTED',
  needs_review: 'NEEDS REVIEW',
  accepted: 'ACCEPTED',
  awaiting_shipment: 'AWAITING SHIPMENT',
  in_transit: 'IN TRANSIT',
  received: 'RECEIVED · READY TO LIST',
  listed: 'LISTED',
  sold: 'SOLD',
  returned: 'RETURNED',
  rejected: 'REJECTED',
  expired: 'EXPIRED',
};

export default function AdminConsignment() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState({ items: [], stats: null });
  const [payouts, setPayouts] = useState({ pending: [], history: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('');
  const [view, setView] = useState('items'); // items | payouts
  const [editing, setEditing] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.set('status', filter);
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/consignment?${params.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/consignment/payouts`, { headers: getAuthHeader() }),
      ]);
      setData(a.data || { items: [], stats: null });
      setPayouts(b.data || { pending: [], history: [] });
    } catch {
      toast.error('FAILED TO LOAD');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateItem = async (itemId, body) => {
    try {
      await axios.patch(`${API}/admin/consignment/${itemId}`, body, { headers: getAuthHeader() });
      toast.success('UPDATED');
      load();
      setEditing(null);
    } catch (e) {
      toast.error(errMsg(e, 'UPDATE FAILED'));
    }
  };

  const listAsProduct = async (itemId) => {
    if (!window.confirm('Create a product listing from this consignment? This makes it live on the site.')) return;
    try {
      const r = await axios.post(`${API}/admin/consignment/${itemId}/list`, {}, { headers: getAuthHeader() });
      toast.success(`LISTED · Product ${r.data.product_id.slice(0, 8)}…`);
      load();
    } catch (e) {
      toast.error(errMsg(e, 'LIST FAILED'));
    }
  };

  const processPayout = async (payoutId) => {
    if (!window.confirm('Mark this payout as SENT? This deducts from the consignor wallet and emails them.')) return;
    try {
      await axios.post(`${API}/admin/consignment/payouts/${payoutId}/process`, {}, { headers: getAuthHeader() });
      toast.success('PAYOUT PROCESSED');
      load();
    } catch (e) {
      toast.error(errMsg(e, 'PAYOUT FAILED'));
    }
  };

  const deleteItem = async (itemId, title) => {
    if (!window.confirm(`Delete consignment "${title}"? This also removes the linked inbound package. SOLD items can't be deleted.`)) return;
    try {
      await axios.delete(`${API}/admin/consignment/${itemId}`, { headers: getAuthHeader() });
      toast.success('CONSIGNMENT DELETED');
      load();
    } catch (e) {
      toast.error(errMsg(e, 'DELETE FAILED'));
    }
  };


  const stats = data.stats || { by_status: {}, total: 0, total_liability: 0, consignors: 0, payouts_pending: 0 };
  const filteredItems = useMemo(() => data.items || [], [data.items]);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="sticky top-0 z-10 bg-black border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-zinc-900"><ArrowLeft size={20} /></Link>
            <div>
              <h1 className="text-lg font-bold font-mono tracking-widest flex items-center gap-2">
                <Handshake size={18} /> CONSIGNMENT
              </h1>
              <p className="text-xs text-zinc-500 font-mono">
                {stats.total} ITEMS · {stats.consignors} CONSIGNORS · ${stats.total_liability.toLocaleString()} LIABILITY
              </p>
            </div>
          </div>
          <button onClick={load} disabled={refreshing} className="p-2 hover:bg-zinc-900" data-testid="refresh-consignment">
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* View toggle */}
        <div className="flex gap-2">
          <button onClick={() => setView('items')} className={`px-4 py-2 font-mono text-xs tracking-wider ${view === 'items' ? 'bg-tiffany-500 text-black' : 'bg-zinc-900 text-zinc-400'}`} data-testid="view-items">
            ITEMS · {stats.total}
          </button>
          <button onClick={() => setView('payouts')} className={`px-4 py-2 font-mono text-xs tracking-wider ${view === 'payouts' ? 'bg-tiffany-500 text-black' : 'bg-zinc-900 text-zinc-400'}`} data-testid="view-payouts">
            PAYOUT REQUESTS · {payouts.pending.length}
          </button>
        </div>

        {view === 'items' && (
          <>
            {/* Status pills */}
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setFilter('')} className={`px-3 py-1.5 font-mono text-[11px] tracking-wider border ${filter === '' ? 'border-tiffany-500 text-tiffany-400 bg-tiffany-500/10' : 'border-zinc-700 text-zinc-400'}`}>
                ALL · {stats.total}
              </button>
              {Object.entries(stats.by_status).map(([k, v]) => (
                <button key={k} onClick={() => setFilter(filter === k ? '' : k)} className={`px-3 py-1.5 font-mono text-[11px] tracking-wider border ${filter === k ? 'border-tiffany-500 text-tiffany-400 bg-tiffany-500/10' : 'border-zinc-700 text-zinc-400'}`} data-testid={`filter-${k}`}>
                  {k.toUpperCase()} · {v}
                </button>
              ))}
            </div>

            {loading ? <p className="text-center py-12 font-mono text-zinc-500">LOADING_</p>
            : filteredItems.length === 0 ? <p className="text-center py-16 font-mono text-zinc-500">NO ITEMS</p>
            : (
              <div className="space-y-3">
                {filteredItems.map(item => {
                  const c = STATUS_COLORS[item.status] || STATUS_COLORS.submitted;
                  const photos = item.photos || [];
                  const photo = photos[0];
                  const photoUrl = photo ? (photo.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : photo) : null;
                  const hasCounter = !!item.counter_offer_price;
                  return (
                    <div key={item.id} className={`border ${c.border} bg-zinc-950 p-4 flex gap-4`} data-testid={`cons-item-${item.id}`}>
                      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-black border border-zinc-800 shrink-0 relative">
                        {photoUrl ? (
                          <img
                            src={photoUrl}
                            alt={item.title}
                            className="w-full h-full object-cover cursor-zoom-in"
                            onClick={() => setLightbox({ images: photos, initialIndex: 0 })}
                            data-testid={`photo-${item.id}`}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-zinc-700 text-[10px]">NO IMG</div>
                        )}
                        {photos.length > 1 && (
                          <span className="absolute bottom-0 right-0 bg-black/80 text-zinc-300 font-mono text-[9px] px-1 pointer-events-none">+{photos.length - 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 ${c.bg} ${c.text} font-mono text-[10px] tracking-widest`}>{STATUS_LABEL[item.status] || item.status.toUpperCase()}</span>
                          {item.brand && <span className="px-2 py-0.5 bg-purple-500/10 text-purple-300 font-mono text-[10px] tracking-widest">{item.brand}</span>}
                          {item.seller_tracking_number && (
                            <span className="px-2 py-0.5 bg-blue-500/10 text-blue-300 font-mono text-[10px] tracking-widest">TRACKING SUBMITTED</span>
                          )}
                        </div>
                        <p className="font-mono text-sm text-white truncate">{item.title}</p>
                        <p className="font-mono text-[11px] text-zinc-500">{item.user_name} · {item.user_email}</p>
                        <div className="flex flex-wrap gap-3 text-[11px] font-mono">
                          <span className="text-zinc-500">Suggested <span className="text-zinc-300">${item.suggested_price?.toFixed(2)}</span></span>
                          {typeof item.final_price === 'number' && <span className="text-tiffany-400">Final ${item.final_price.toFixed(2)}</span>}
                          {typeof item.sold_price === 'number' && <span className="text-purple-400">Sold ${item.sold_price.toFixed(2)} · paid ${item.seller_earnings?.toFixed(2)}</span>}
                          <span className="text-zinc-500">{item.commission_pct ?? 30}% · floor ${item.base_floor ?? 10}</span>
                        </div>

                        {hasCounter && (
                          <div className="bg-yellow-500/10 border border-yellow-500/40 px-2 py-1.5 font-mono text-[11px]">
                            <span className="text-yellow-400 tracking-widest">COUNTER FROM SELLER · </span>
                            <span className="text-white">${item.counter_offer_price.toFixed(2)}</span>
                            {item.counter_offer_note && <span className="text-zinc-400"> · "{item.counter_offer_note}"</span>}
                          </div>
                        )}

                        <div className="flex gap-2 flex-wrap pt-1">
                          {/* Single Edit button replaces redundant Accept/Edit. The modal handles all term setting + accept/reject. */}
                          {item.status !== 'sold' && item.status !== 'rejected' && (
                            <button onClick={() => setEditing(item)} className="px-3 py-1 bg-tiffany-500 text-black font-mono text-[10px] tracking-wider hover:bg-tiffany-400" data-testid={`edit-${item.id}`}>
                              <Check size={11} className="inline mr-1 -mt-0.5" /> {item.status === 'submitted' || item.status === 'needs_review' ? 'REVIEW / ACCEPT' : 'EDIT'}
                            </button>
                          )}
                          {item.status === 'submitted' && (
                            <button onClick={() => updateItem(item.id, { status: 'rejected' })} className="px-3 py-1 border border-red-500/40 text-red-400 font-mono text-[10px] tracking-wider hover:bg-red-500/10" data-testid={`reject-${item.id}`}>
                              <X size={11} className="inline mr-1 -mt-0.5" /> REJECT
                            </button>
                          )}
                          {item.status === 'awaiting_shipment' && (
                            <button onClick={() => updateItem(item.id, { status: 'received' })} className="px-3 py-1 bg-purple-500 text-black font-mono text-[10px] tracking-wider hover:bg-purple-400" data-testid={`mark-received-${item.id}`}>
                              MARK RECEIVED
                            </button>
                          )}
                          {item.status === 'received' && (
                            <button
                              onClick={() => {
                                // For consignment listings, the platform's "cost" is what we
                                // owe the seller (≈ base_floor) — NOT the sale price. The
                                // admin can still tweak this in the product form.
                                const sellerPayout = (
                                  item.base_floor != null
                                    ? item.base_floor
                                    : (item.final_price || item.suggested_price || 0) * 0.30
                                );
                                const data = {
                                  name: item.title || '',
                                  description: item.description || '',
                                  category: item.category || '',
                                  brand: item.brand || '',
                                  images: item.photos || [],
                                  cost: parseFloat(sellerPayout || 0).toFixed(2),
                                  price: (item.final_price || item.suggested_price || 0).toFixed(2),
                                  // These three are critical — they tag the resulting product
                                  // so process_consignment_sale() credits the seller's wallet
                                  // when the product sells.
                                  is_consignment: true,
                                  consignment_item_id: item.id,
                                  consignor_user_id: item.user_id,
                                  // Display-only context for the prefill banner
                                  _consignment_meta: {
                                    seller_name: item.user_name || item.user_email || '',
                                    seller_email: item.user_email || '',
                                    base_floor: item.base_floor,
                                    commission_pct: item.commission_pct,
                                    suggested_price: item.suggested_price,
                                  },
                                };
                                sessionStorage.setItem('prefill_product', JSON.stringify(data));
                                window.location.href = '/admin?tab=products&action=create';
                              }}
                              className="px-3 py-1 bg-tiffany-500 text-black font-mono text-[10px] tracking-wider hover:bg-tiffany-400"
                              data-testid={`create-listing-${item.id}`}
                            >
                              <ExternalLink size={11} className="inline mr-1 -mt-0.5" /> CREATE LISTING
                            </button>
                          )}
                          {item.product_id && (
                            <Link to={`/product/${item.product_id}`} className="px-3 py-1 border border-zinc-700 text-zinc-300 font-mono text-[10px] tracking-wider hover:border-tiffany-500" data-testid={`view-product-${item.id}`}>
                              VIEW LISTING →
                            </Link>
                          )}
                          {item.expires_at && item.status === 'listed' && (
                            <span className="px-2 py-1 font-mono text-[10px] text-zinc-500">
                              EXPIRES {new Date(item.expires_at).toLocaleDateString()}
                            </span>
                          )}
                          {/* DELETE — always available except SOLD */}
                          {item.status !== 'sold' && (
                            <button
                              onClick={() => deleteItem(item.id, item.title)}
                              className="px-3 py-1 border border-red-500/30 text-red-400/70 hover:border-red-500 hover:text-red-400 hover:bg-red-500/10 font-mono text-[10px] tracking-wider ml-auto"
                              data-testid={`delete-${item.id}`}
                              title="Delete this consignment + its inbound row"
                            >
                              DELETE
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {view === 'payouts' && (
          <div className="space-y-3">
            {payouts.pending.length === 0
              ? <p className="text-center py-12 font-mono text-zinc-500">NO PENDING PAYOUTS</p>
              : payouts.pending.map(p => (
                <div key={p.id} className="border border-yellow-500/40 bg-zinc-950 p-4 flex items-center justify-between gap-3" data-testid={`payout-${p.id}`}>
                  <div className="min-w-0">
                    <p className="font-mono text-sm text-white truncate">{p.user_email}</p>
                    <p className="font-mono text-[11px] text-zinc-500">REQUESTED {new Date(p.requested_at).toLocaleString()} · VIA {p.method.toUpperCase()}</p>
                    {p.note && <p className="font-mono text-[11px] text-zinc-400 italic mt-1">"{p.note}"</p>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <p className="font-mono text-lg font-bold text-tiffany-500"><DollarSign className="w-3 h-3 inline -mt-1" />{p.amount.toFixed(2)}</p>
                    <button onClick={() => processPayout(p.id)} className="px-3 py-2 bg-tiffany-500 text-black font-mono text-[10px] tracking-wider hover:bg-tiffany-400" data-testid={`process-${p.id}`}>
                      <Send size={11} className="inline mr-1 -mt-0.5" /> MARK SENT
                    </button>
                  </div>
                </div>
              ))}
            {payouts.history.length > 0 && (
              <>
                <p className="font-mono text-[11px] text-zinc-500 tracking-widest pt-6">PAYOUT HISTORY</p>
                {payouts.history.slice(0, 30).map(p => (
                  <div key={p.id} className="border border-zinc-800 bg-zinc-950 p-3 flex items-center justify-between text-[11px] font-mono">
                    <span className="text-zinc-300">{p.user_email}</span>
                    <span className="text-tiffany-400">${p.amount.toFixed(2)} · {p.method.toUpperCase()}</span>
                    <span className="text-zinc-500">{p.processed_at ? new Date(p.processed_at).toLocaleDateString() : '—'}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {editing && <EditModal item={editing} onClose={() => setEditing(null)} onSave={(body) => updateItem(editing.id, body)} />}
      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          initialIndex={lightbox.initialIndex}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

function EditModal({ item, onClose, onSave }) {
  // Base floor defaults to 30% of suggested price if not yet set on the item.
  const defaultFloor = item.base_floor ?? Math.round((item.suggested_price || 0) * 0.30 * 100) / 100;
  const initialFinal = item.final_price ?? item.suggested_price;
  const initialCommission = item.commission_pct ?? 30;
  const initialFloor = defaultFloor;

  // For submitted items, the "current" status is 'submitted' but the user will
  // typically transition to 'accepted' (no changes) or 'needs_review' (changes)
  // — we let our save logic detect that automatically.
  const isInitialReview = item.status === 'submitted' || item.status === 'needs_review';
  const [form, setForm] = useState({
    status: item.status,
    final_price: initialFinal,
    commission_pct: initialCommission,
    base_floor: initialFloor,
    admin_notes: item.admin_notes || '',
  });
  const [showFloorInfo, setShowFloorInfo] = useState(false);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Detect whether the admin has changed any term (only matters during initial review)
  const fp = parseFloat(form.final_price) || 0;
  const cp = parseFloat(form.commission_pct) || 0;
  const bf = parseFloat(form.base_floor) || 0;
  const termsChanged = (
    Math.abs(fp - (initialFinal || 0))         > 0.01 ||
    Math.abs(cp - (initialCommission || 0))    > 0.01 ||
    Math.abs(bf - (initialFloor || 0))         > 0.01
  );

  // Split preview
  const sample = fp;
  const pctSellerCut   = sample * (1 - cp / 100);
  const floorSellerCut = Math.max(0, sample - bf);
  const sellerEarn     = Math.min(pctSellerCut, floorSellerCut);
  const platformCut    = sample - sellerEarn;
  const floorWon       = floorSellerCut < pctSellerCut && sample > 0;

  // Save behavior:
  //   • Initial review (submitted/needs_review) + no changes → status=accepted (button = green "ACCEPT")
  //   • Initial review + changes → status=accepted (backend routes to needs_review automatically)
  //   • Anything else → respect whatever status the dropdown is set to.
  const handleSave = () => {
    let outStatus = form.status;
    if (isInitialReview) {
      outStatus = 'accepted';
    }
    onSave({
      status: outStatus,
      final_price: parseFloat(form.final_price),
      commission_pct: parseFloat(form.commission_pct),
      base_floor: parseFloat(form.base_floor),
      admin_notes: form.admin_notes,
    });
  };

  const saveLabel = isInitialReview
    ? (termsChanged ? 'SEND COUNTER TO SELLER' : 'ACCEPT')
    : 'SAVE';
  const saveColor = isInitialReview
    ? (termsChanged ? 'bg-yellow-500 hover:bg-yellow-400' : 'bg-green-500 hover:bg-green-400')
    : 'bg-teal-500 hover:bg-teal-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-modal">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-bold tracking-wider font-mono">EDIT CONSIGNMENT</h2>
            <p className="text-xs text-zinc-500 font-mono truncate uppercase">{item.title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Status — hidden while in submitted/needs_review state since the
              save button auto-routes the status. Visible for editing post-acceptance. */}
          {!isInitialReview && (
            <div>
              <label className="block text-xs text-zinc-500 mb-1 font-mono">STATUS *</label>
              <ModalDropdown
                value={form.status}
                onChange={(v) => set('status', v)}
                options={[
                  { value: 'accepted',          label: 'ACCEPTED (LEGACY)' },
                  { value: 'awaiting_shipment', label: 'AWAITING SHIPMENT' },
                  { value: 'received',          label: 'RECEIVED' },
                  { value: 'listed',            label: 'LISTED' },
                  { value: 'sold',              label: 'SOLD' },
                  { value: 'needs_review',      label: 'NEEDS REVIEW' },
                  { value: 'rejected',          label: 'REJECTED' },
                  { value: 'returned',          label: 'RETURNED' },
                  { value: 'expired',           label: 'EXPIRED' },
                ]}
                testId="edit-status"
              />
            </div>
          )}

          {/* Final price */}
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">FINAL LISTING PRICE ($) *</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.final_price ?? ''}
              onChange={(e) => set('final_price', e.target.value)}
              placeholder="0.00"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none font-mono"
              data-testid="edit-final-price"
            />
          </div>

          {/* Commission + Floor */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-zinc-500 mb-1 font-mono">COMMISSION %</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={form.commission_pct ?? ''}
                onChange={(e) => set('commission_pct', e.target.value)}
                placeholder="30"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none font-mono"
                data-testid="edit-commission"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 font-mono flex items-center gap-1">
                BASE FLOOR ($)
                <button
                  type="button"
                  onClick={() => setShowFloorInfo(s => !s)}
                  className="w-3.5 h-3.5 rounded-full border border-zinc-600 text-zinc-500 hover:border-teal-500 hover:text-teal-500 text-[8px] font-bold flex items-center justify-center leading-none"
                  data-testid="floor-info-btn"
                  aria-label="What is base floor?"
                >i</button>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.base_floor ?? ''}
                onChange={(e) => set('base_floor', e.target.value)}
                placeholder="10"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none font-mono"
                data-testid="edit-floor"
              />
            </div>
          </div>

          {showFloorInfo && (
            <div className="bg-zinc-800/50 border border-teal-500/30 rounded-lg p-3 space-y-1 font-mono text-[11px] text-zinc-300 leading-relaxed" data-testid="floor-info-panel">
              <p className="text-teal-400 tracking-widest mb-1">WHAT IS "BASE FLOOR"?</p>
              <p>THE <strong>MINIMUM</strong> DOLLAR AMOUNT BLK MRKT KEEPS ON A SALE. THE BIGGER OF THE TWO WINS:</p>
              <p>• COMMISSION % &nbsp;&nbsp;OR&nbsp;&nbsp; • BASE FLOOR $</p>
              <p className="text-zinc-500 mt-1">EXAMPLE: $20 ITEM, 30% / $10 FLOOR → 30% = $6 (TOO LOW) | FLOOR $10 (WINS). PROTECTS YOU ON CHEAP ITEMS WHERE THE % WOULD BE TOO SMALL.</p>
            </div>
          )}

          {/* Admin notes */}
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">ADMIN NOTES (PRIVATE)</label>
            <textarea
              value={form.admin_notes || ''}
              onChange={(e) => set('admin_notes', e.target.value)}
              placeholder="INTERNAL NOTES, REJECTION REASON, ETC."
              rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none resize-none font-mono uppercase"
              data-testid="edit-notes"
            />
          </div>

          {/* Split preview */}
          {sample > 0 && (
            <div className="bg-zinc-800 rounded-lg p-3 space-y-2" data-testid="split-preview">
              <p className="text-[11px] text-zinc-500 tracking-widest font-mono">SPLIT PREVIEW @ ${sample.toFixed(2)}</p>
              <div className="grid grid-cols-2 gap-3 font-mono text-xs">
                <div className="text-center">
                  <p className="text-teal-400 text-base font-bold">${sellerEarn.toFixed(2)}</p>
                  <p className="text-[10px] text-zinc-500 tracking-wider">SELLER ({((sellerEarn / sample) * 100).toFixed(1)}%)</p>
                </div>
                <div className="text-center">
                  <p className="text-purple-400 text-base font-bold">${platformCut.toFixed(2)}</p>
                  <p className="text-[10px] text-zinc-500 tracking-wider">PLATFORM ({((platformCut / sample) * 100).toFixed(1)}%)</p>
                </div>
              </div>
              <p className="text-[10px] text-zinc-600 font-mono">
                {floorWon ? 'FLOOR APPLIED — % CUT WOULD BE TOO SMALL ON THIS PRICE.' : 'COMMISSION % APPLIED — LARGER PLATFORM CUT.'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-zinc-900 border-t border-zinc-800 p-4 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-700 rounded-lg text-zinc-300 font-mono text-xs tracking-widest hover:bg-zinc-800">CANCEL</button>
          <button
            onClick={handleSave}
            className={`px-5 py-2 ${saveColor} text-black font-mono text-xs font-bold tracking-widest rounded-lg`}
            data-testid="save-edit"
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalDropdown({ value, onChange, options, testId }) {
  const [open, setOpen] = useState(false);
  const display = options.find(o => o.value === value)?.label;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-left font-mono uppercase flex items-center justify-between focus:border-teal-500 focus:outline-none text-white"
        data-testid={testId}
      >
        <span>{display || ''}</span>
        <ChevronDown size={16} className={`text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 w-full bg-zinc-900 border border-zinc-700 rounded-lg max-h-56 overflow-y-auto shadow-xl mt-1">
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`w-full px-3 py-2 text-left font-mono uppercase text-sm border-b border-zinc-800 last:border-0 ${
                  value === opt.value ? 'bg-teal-500/20 text-teal-400' : 'text-white hover:bg-zinc-800'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
